import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import AppCta from '../components/AppCta';
import useFadeIn from '../hooks/useFadeIn';
import appHomeScreen from '../assets/images/hero/app-home-screen.jpg';

const HowItWorks: React.FC = () => {
  useFadeIn();

  const steps = [
    {
      number: '01',
      title: 'Add Your Properties',
      description: 'Set up your properties — single or multi-unit. Choose from House, Apartment, Condominium, Retail, Office, or Parking. Add photos, amenities, bedrooms, bathrooms, and rental terms.',
      icon: 'fas fa-home'
    },
    {
      number: '02',
      title: 'Add Tenants & Create Agreements',
      description: 'Build complete tenant profiles with emergency contacts and communication preferences. Create lease agreements with AI-drafted terms, move-in/out checklists, and a shareable link so tenants can view online.',
      icon: 'fas fa-file-contract'
    },
    {
      number: '03',
      title: 'Track Payments & Maintenance',
      description: 'Log rent and other payments with auto-calculated due dates. Generate PDF receipts with QR codes and email them to tenants. Log maintenance requests by category and priority — track from pending to resolved.',
      icon: 'fas fa-credit-card'
    }
  ];

  const detailedFeatures = [
    {
      icon: 'fa fa-chart-bar',
      title: 'Rent Collection Reports',
      description: 'Generate rent collection reports with summaries and trends. Track revenue performance across all your properties and download as PDF for accounting.'
    },
    {
      icon: 'fa fa-bell',
      title: 'Smart Notifications',
      description: 'Get reminders before rent collection dates, alerts for agreements expiring within 60 days, and activity notifications for all key changes.'
    },
    {
      icon: 'fa fa-file-pdf',
      title: 'PDF Receipts & Documents',
      description: 'Download or email payment receipts as PDF with QR codes. Export property inventory checklists and rent collection reports as PDFs.'
    },
    {
      icon: 'fa fa-robot',
      title: 'AI Agreement Drafts',
      description: 'Generate a complete rental agreement in minutes using AI. Customize clauses to fit your property and tenant, then export as a professional PDF.'
    }
  ];

  const benefits = [
    {
      icon: 'fa fa-mobile-alt',
      title: 'Mobile Accessibility',
      description: 'Manage everything from your phone. Download eTENANT from Google Play and start instantly — no setup required.'
    },
    {
      icon: 'fa fa-shield-alt',
      title: 'Secure & Private',
      description: 'Encrypted cloud storage. EXIF metadata automatically removed from photos. Compliant with global data privacy standards.'
    },
    {
      icon: 'fa fa-building',
      title: 'Multi-Unit Support',
      description: 'Manage entire buildings — assign tenants per unit, track occupancy per floor, and handle individual unit rental terms.'
    },
    {
      icon: 'fa fa-link',
      title: 'Tenant Access Link',
      description: 'Share a unique link so tenants can view their rental agreement online — no app required on their end.'
    }
  ];

  const tools = [...detailedFeatures, ...benefits];

  const propertyTypes = [
    { icon: 'fas fa-house-user', label: 'House' },
    { icon: 'fas fa-building', label: 'Apartment' },
    { icon: 'fas fa-city', label: 'Condominium' },
    { icon: 'fas fa-store', label: 'Retail' },
    { icon: 'fas fa-briefcase', label: 'Office' },
    { icon: 'fas fa-car', label: 'Parking' }
  ];

  const highlights = [
    'Single and multi-unit property support',
    'AI-powered rental agreement drafting',
    'PDF receipts with QR codes, emailed to tenants',
    'Google Drive document storage integration'
  ];

  return (
    <div>
      <SEO
        title="How It Works: Manage Rentals in 3 Simple Steps"
        description="See how eTENANT works: add your properties, add tenants and rental agreements, then track rent payments and maintenance — all from your phone."
        path="/how-it-works"
        breadcrumb="How It Works"
      />

      <PageHero
        crumb="How It Works"
        eyebrow="How it works"
        title={<>Managing Rentals, <span>Made Simple</span></>}
        subtitle="From setup to rent collection — eTENANT guides you through the whole process in three easy steps."
      />

      {/* Steps */}
      <section className="hiw-steps">
        <div className="container">
          <div className="section-v2-header fade-in-up">
            <span className="features-v2-eyebrow">3 simple steps</span>
            <h2 className="features-v2-title">
              Get Started in <span>Minutes</span>
            </h2>
            <p className="features-v2-subtitle">
              No setup fees, no training needed — just download the app and follow along.
            </p>
          </div>

          <div className="hiw-steps-track">
            {steps.map((step, index) => (
              <div key={step.number} className={`hiw-step fade-in-up delay-${index + 1}`}>
                <div className="hiw-step-marker">
                  <span>{step.number}</span>
                </div>
                <div className="hiw-step-card">
                  <span className="features-v2-icon">
                    <i className={step.icon} aria-hidden="true"></i>
                  </span>
                  <h3 className="features-v2-card-title">{step.title}</h3>
                  <p className="features-v2-card-text">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview with app preview */}
      <section className="hiw-overview">
        <div className="container">
          <div className="row align-items-center g-4 g-lg-5">
            <div className="col-lg-6 order-lg-2">
              <div className="hiw-phone-wrap fade-in-up">
                <span className="hiw-phone-blob" aria-hidden="true"></span>
                <div className="hiw-phone">
                  <img src={appHomeScreen} alt="eTENANT app home screen" loading="lazy" />
                  <span className="hiw-phone-island" aria-hidden="true"></span>
                </div>
                <div className="hiw-float hiw-float-1">
                  <span className="hiw-float-icon"><i className="fas fa-receipt" aria-hidden="true"></i></span>
                  <span><strong>PDF receipt</strong>sent to tenant</span>
                </div>
                <div className="hiw-float hiw-float-2">
                  <span className="hiw-float-icon"><i className="fas fa-robot" aria-hidden="true"></i></span>
                  <span><strong>AI agreement</strong>drafted in minutes</span>
                </div>
              </div>
            </div>

            <div className="col-lg-6 order-lg-1">
              <div className="hiw-overview-content fade-in-up delay-1">
                <span className="features-v2-eyebrow hiw-eyebrow-left">Overview</span>
                <h2 className="features-v2-title text-start">
                  Built for Property Owners <span>&amp; Landlords</span>
                </h2>
                <p className="hiw-text">
                  Whether you own a single home or a multi-unit building, eTENANT keeps everything organized in one place — tenants, agreements, payments, maintenance, and documents.
                </p>
                <p className="hiw-text">
                  Start with Guest Mode — no account needed. Sign in with Google or email anytime to sync your data across devices.
                </p>
                <ul className="hiw-checks">
                  {highlights.map((item) => (
                    <li key={item}>
                      <i className="fas fa-check" aria-hidden="true"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tools & benefits */}
      <section className="hiw-tools">
        <div className="container">
          <div className="section-v2-header fade-in-up">
            <span className="features-v2-eyebrow">Features</span>
            <h2 className="features-v2-title">
              Powerful Tools for <span>Every Stage</span>
            </h2>
            <p className="features-v2-subtitle">
              From lease creation to rent collection, maintenance tracking to inventory management.
            </p>
          </div>

          <div className="row g-4">
            {tools.map((tool, index) => (
              <div key={tool.title} className="col-xl-3 col-md-6">
                <div className={`features-v2-card fade-in-up delay-${(index % 3) + 1}`}>
                  <div className="features-v2-card-top">
                    <span className="features-v2-icon">
                      <i className={tool.icon} aria-hidden="true"></i>
                    </span>
                  </div>
                  <h3 className="features-v2-card-title">{tool.title}</h3>
                  <p className="features-v2-card-text">{tool.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="features-v2-more">
            <Link to="/features" className="features-v2-more-btn">
              Explore all features
              <i className="fas fa-arrow-right" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Property types */}
      <section className="hiw-types">
        <div className="container">
          <div className="section-v2-header fade-in-up">
            <span className="features-v2-eyebrow">Property types</span>
            <h2 className="features-v2-title">
              Supports All Rental <span>Property Types</span>
            </h2>
            <p className="features-v2-subtitle">Manage any type of rental property from a single app.</p>
          </div>

          <div className="hiw-types-grid">
            {propertyTypes.map((type, index) => (
              <div key={type.label} className={`hiw-type fade-in-up delay-${(index % 3) + 1}`}>
                <span className="features-v2-icon">
                  <i className={type.icon} aria-hidden="true"></i>
                </span>
                <span className="hiw-type-label">{type.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AppCta
        eyebrow="Ready to get started?"
        title="Download eTENANT and Start Today"
        text="Download eTENANT from Google Play and start managing your rentals today."
      />
    </div>
  );
};

export default HowItWorks;
